'use client'

import {
  Input,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react'
import clsx from 'clsx'
import countries from 'flag-icons/country.json'
import 'flag-icons/css/flag-icons.min.css'
import countryCodes from 'lib/settings/country-codes'
import { ControlledFormField, FormFieldCommonProps } from 'lib/types/Form'
import {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Controller } from 'react-hook-form'
import Icon from '../../Icon/Icon'
import InputError from '../InputError/InputError'
import InputLabel from '../InputLabel/InputLabel'

const filteredCountries = countries.filter(
  ({ code }) => !/x+/.test(code) && code in countryCodes,
)

const codeToCountry: Record<string, countryCodeKeys> = Object.entries(
  countryCodes,
).reduce(
  (all, [country, numberCode]) => {
    all[numberCode] = country as countryCodeKeys
    return all
  },
  {} as Record<string, countryCodeKeys>,
)

const validateUSNumber = (phoneNumber: string = '') => {
  if (/^[+]1/.test(phoneNumber)) {
    const digits = phoneNumber.replace(/\D/g, '').replace(/^1/, '')
    if (digits.length === 0) return 'Phone is required'
    return digits.length !== 10 ? 'US number must be exactly 10 digits' : true
  }
  return true
}

type countryCodeKeys = keyof typeof countryCodes

interface PhoneInputCountryOption {
  code: countryCodeKeys
  name: string
  numberCode: string
}

export interface PhoneInputStandaloneProps
  extends Omit<FormFieldCommonProps, 'onChange'> {
  placeholder?: string
  priorityCountries?: countryCodeKeys[]
  onChange?: (data: string | number, fieldName?: string) => void
  controlled?: boolean
  // Lock the input to US (+1) and hide the country picker entirely — used by
  // the SMS opt-in surfaces, which only accept US/NANP numbers.
  usOnly?: boolean
}

export interface PhoneInputProps
  extends ControlledFormField,
    PhoneInputStandaloneProps {
  /**
   * Additional validation composed AFTER the built-in US length check. The
   * `rules.validate` key is overwritten by that built-in check, so this is
   * the supported hook for consumer validation (e.g. the shared NANP/E.164
   * validator). Receives the field value and the current form values.
   */
  extraValidate?: (
    value: string | number,
    formValues: Record<string, unknown>,
  ) => string | true
}

// Standalone component that can be used outside react-hook-form's context
export const PhoneInputStandalone = forwardRef<
  HTMLInputElement,
  PhoneInputStandaloneProps
>(function PhoneInputStandaloneWithRef(
  {
    id,
    name,
    placeholder,
    label,
    className,
    error = '',
    onChange,
    defaultValue,
    priorityCountries,
    disabled,
    controlled,
    usOnly,
    'aria-label': ariaLabel,
  }: PhoneInputStandaloneProps,
  ref,
) {
  let countryOptions: PhoneInputCountryOption[] = filteredCountries.map(
    (country) => ({
      ...country,
      code: country.code as countryCodeKeys,
      numberCode: countryCodes[country.code as countryCodeKeys],
    }),
  )

  // When locked to US, collapse the list to a single option so the selector can
  // never resolve to another locale, even if the dropdown is somehow triggered.
  if (usOnly) {
    countryOptions = countryOptions.filter(({ code }) => code === 'us')
  }

  let priorityCountryArr: PhoneInputCountryOption[] = []
  priorityCountries?.forEach(
    (countryCode) =>
      (priorityCountryArr = [
        ...priorityCountryArr,
        ...countryOptions.filter(({ code }) => code === countryCode),
      ]),
  )

  countryOptions = [
    ...priorityCountryArr,
    ...countryOptions.filter(
      ({ code }) => !priorityCountries?.some((a) => a === code),
    ),
  ]

  const [defaultValueCached, setDefaultValueCached] = useState(defaultValue)
  const [defaultCountry, defaultNumber] = useMemo(() => {
    if (defaultValueCached) {
      let countryCode = '+1'
      const stringifiedDefault = String(defaultValueCached ?? '')
      let number = stringifiedDefault

      if (/^\+/.test(stringifiedDefault.trim())) {
        // has explicit country code
        const trimmedDefault = stringifiedDefault.trim()
        const [first, ...rest] = trimmedDefault.split(/\s+/)
        countryCode = first
        number = rest.join(' ')

        if (!number && first.length < trimmedDefault.length) {
          number = trimmedDefault.slice(first.length).trim()
        }

        if (!number) {
          // Compact E.164 (`+19126140403`) — no separator between the country
          // code and the number. Match the longest known calling code prefix.
          const digits = trimmedDefault.replace(/[^\d]/g, '')
          for (let len = 3; len >= 1; len--) {
            const prefix = digits.slice(0, len)
            if (codeToCountry[prefix]) {
              countryCode = `+${prefix}`
              number = digits.slice(len)
              break
            }
          }
        }
      }

      const countryNumber = countryCode.replace(/[^\d]/g, '') as countryCodeKeys
      return [codeToCountry[countryNumber] || 'us', number.trim()]
    }

    return ['us' as countryCodeKeys, '']
  }, [defaultValueCached])

  const getCountryNumberCode = useCallback(
    (country: countryCodeKeys) =>
      country && countryCodes[country] ? countryCodes[country] : '1',
    [],
  )
  const [selectedCountry, setSelectedCountry] = useState<countryCodeKeys>(
    defaultCountry || 'us',
  )
  const [textInputValue, setTextInputValue] = useState(defaultNumber || '')
  const [formattedValue, setFormattedValue] = useState(defaultNumber || '')
  const [errorMessage, setErrorMessage] = useState(error || '')

  const formatBasedOnCountry = useCallback(
    (
      value: string = '',
      countryCode: countryCodeKeys = selectedCountry,
    ): string => {
      const numbersOnly = value.replace(/[^\d]/g, '')

      return countryCode === 'us'
        ? numbersOnly
            .match(/(\d{1,3})(\d{1,3})?(\d{1,4})?/)
            ?.slice(1)
            .filter(Boolean)
            .join('-') || ''
        : value
    },
    [selectedCountry],
  )

  const fireOnChange = useCallback(
    (selectedCountry: countryCodeKeys, textInputValue: string) => {
      const currentValue =
        selectedCountry && textInputValue
          ? `+${getCountryNumberCode(selectedCountry)} ${textInputValue}`
          : ''

      if (controlled) {
        onChange?.(currentValue, name)
      } else {
        if (onChange) {
          const validation = validateUSNumber(currentValue)
          if (typeof validation === 'string') {
            onChange(0, name)
            setErrorMessage(validation)
          } else {
            onChange(currentValue, name)
            setErrorMessage('')
          }
        }
      }
    },
    [getCountryNumberCode, name, controlled, onChange],
  )

  const onTextInput = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      // Remove invalid characters
      const acceptedCharsOnly = String(target.value).replace(/[^0-9-]/g, '')
      const formatted = formatBasedOnCountry(acceptedCharsOnly)
      setFormattedValue(formatted)
      setTextInputValue(formatted)
      fireOnChange(selectedCountry, formatted)
    },
    [fireOnChange, formatBasedOnCountry, selectedCountry],
  )

  const onTextChange = useCallback(() => {
    setTextInputValue(formattedValue)
    fireOnChange(selectedCountry, formattedValue)
  }, [formattedValue, selectedCountry, fireOnChange])

  const onCountryChange = useCallback(
    (countryCode: countryCodeKeys) => {
      setSelectedCountry(countryCode)
      setTextInputValue('')
      setFormattedValue('')
      fireOnChange(countryCode, textInputValue)
    },
    [fireOnChange, textInputValue],
  )

  useEffect(() => {
    setErrorMessage(error || '')
  }, [error])

  useEffect(() => {
    setFormattedValue(defaultNumber)
    setTextInputValue(defaultNumber)
  }, [defaultNumber])

  useEffect(() => {
    setSelectedCountry(defaultCountry)
  }, [defaultCountry])

  // Handle defaultValue change from the outside
  useEffect(() => {
    if (defaultValue !== defaultValueCached) {
      setDefaultValueCached(defaultValue)
      onChange?.(defaultValue as string)
    }
  }, [defaultValue, defaultValueCached, onChange])

  return (
    <div className={clsx(`text-input`, className)}>
      <div className="flex items-baseline justify-between">
        {label && <InputLabel htmlFor={id}>{label}</InputLabel>}
      </div>
      <div
        className={clsx(
          'group flex relative w-full text-black bg-white type-body-8 rounded-8 mt-2 shadow-input',
          !disabled && 'hover:shadow-input-active',
          // Grey the field only when it's genuinely disabled — keyed off the
          // `disabled` prop, not `has-[:disabled]`. In `usOnly` mode the country
          // button is disabled to lock the locale to US, and a descendant
          // `:disabled` selector would otherwise grey out the whole (typeable)
          // field. See SmsOptInModal's usOnly PhoneInput.
          disabled && 'cursor-not-allowed bg-neutral-200 text-neutral-600',
          errorMessage && '!shadow-input-error',
        )}
      >
        <Listbox
          defaultValue={usOnly ? 'us' : defaultCountry}
          onChange={onCountryChange}
        >
          <ListboxButton
            className={clsx(
              'select-country',
              'group flex justify-between items-center px-4 w-20 border-r border-r-neutral-500 h-12 lg:h-14',
              disabled && 'opacity-50',
              usOnly && 'cursor-default',
            )}
            aria-label={usOnly ? 'United States' : 'Select country'}
            disabled={disabled || usOnly}
          >
            <span
              className={clsx('fi', `fi-${usOnly ? 'us' : selectedCountry}`)}
            ></span>
            {!usOnly && (
              <Icon
                icon="chevron-down"
                className="w-6 h-6 ml-auto group-data-[open]:rotate-180 text-black"
              />
            )}
          </ListboxButton>
          <div className="input-wrapper w-full">
            <input
              type="text"
              name={name}
              defaultValue=""
              readOnly
              hidden
              aria-hidden="true"
              tabIndex={-1}
            />
            <Input
              type="text"
              className={clsx(
                'flex-1 bg-transparent h-full flex items-center px-4 min-w-[12ch] w-full',
                'disabled:cursor-not-allowed disabled:text-neutral-600',
              )}
              value={formattedValue}
              placeholder={placeholder}
              onInput={onTextInput}
              onBlur={onTextChange}
              ref={ref}
              disabled={disabled}
              aria-label={ariaLabel}
              autoComplete="tel-national"
              data-dd-privacy="mask"
            />
            <div
              className="h-0 px-4 min-w-[12ch] whitespace-nowrap overflow-hidden"
              aria-hidden
            >
              {formattedValue}
            </div>
          </div>
          {!usOnly && (
            <ListboxOptions className="absolute top-full left-0  text-theme-primary border border-neutral-400 rounded-8 mt-2 shadow-dropdown bg-white w-full h-[224px] overflow-auto  z-10">
              {countryOptions.map((country) => (
                <ListboxOption
                  key={country.code}
                  value={country.code}
                  className={clsx(
                    'flex items-center justify-start data-[focus]:bg-neutral-200 px-4 min-h-14 type-body-8 cursor-pointer',
                    '[&:not(:last-child)]:border-b border-neutral-300',
                    country.code === selectedCountry && 'bg-neutral-200',
                  )}
                >
                  <div
                    className="flex items-start"
                    data-country={country.name}
                    data-code={country.code}
                  >
                    <span
                      className={`fi fi-${country.code} mr-2 mt-[2px] shrink-0`}
                    ></span>
                    <span>+{country.numberCode}</span>&nbsp;
                    <span>{country.name}</span>
                  </div>
                </ListboxOption>
              ))}
            </ListboxOptions>
          )}
        </Listbox>
      </div>
      <InputError>{errorMessage}</InputError>
    </div>
  )
})

// Controlled form element that is automatically registered to react-hook-form
export default function PhoneInput({
  name,
  label = '',
  control,
  disabled = false,
  className = '',
  placeholder,
  rules = {},
  priorityCountries,
  onChange,
  shouldUnregister,
  defaultValue,
  extraValidate,
  ...props
}: PhoneInputProps) {
  return (
    <Controller
      name={name || ''}
      control={control}
      shouldUnregister={shouldUnregister}
      defaultValue={defaultValue}
      rules={{
        ...(rules || {}),
        validate: (value, formValues) => {
          if (value === 0) {
            return 'Phone is required'
          }

          const result = validateUSNumber(value)
          if (result !== true) {
            return result
          }

          return extraValidate?.(value, formValues) ?? true
        },
      }}
      render={({
        field: { onChange: fieldOnChange, ...field },
        fieldState,
      }) => {
        const onInputChange = (data: number | string) => {
          fieldOnChange?.(data)
          onChange?.(data, name)
        }

        return (
          <PhoneInputStandalone
            controlled
            {...{
              label,
              className,
              disabled,
              placeholder,
              priorityCountries,
            }}
            {...field}
            {...props}
            defaultValue={defaultValue}
            onChange={onInputChange}
            error={fieldState.error?.message}
          />
        )
      }}
    />
  )
}
